import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import { ibkrService } from '../../services/ibkrService';

interface Leg {
  action: string;
  qty: number;
  strike: number;
  type: string;
  expiry: string;
  entry_price: number;
  market_price: number;
}

interface Position {
  id: string;
  symbol: string;
  strategy: string;
  opened_at: string;
  regime: string;
  pnl: number;
  pnl_pct: number;
  cost: number;
  market_value: number;
  max_profit: number;
  dte: number;
  status: string;
  legs: Leg[];
  reason: string;
  exit_rules?: { label: string; met: boolean }[];
}

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  profit: 'success',
  loss: 'error',
  breakeven: 'warning',
};

interface CloseOpts {
  orderType: 'MKT' | 'LMT' | 'AUTO';
  limitPrice?: number;
  outsideRth: boolean;
}

const PositionRow: React.FC<{
  position: Position;
  onClose: (id: string, opts: CloseOpts) => void;
  closing: boolean;
}> = ({
  position,
  onClose,
  closing,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [orderType, setOrderType] = useState<'MKT' | 'LMT' | 'AUTO'>('AUTO');
  const [limitPrice, setLimitPrice] = useState<string>('');
  const [outsideRth, setOutsideRth] = useState(true);

  const handleDialogConfirm = () => {
    setDialogOpen(false);
    onClose(position.id, {
      orderType,
      limitPrice: orderType === 'LMT' ? parseFloat(limitPrice) : undefined,
      outsideRth,
    });
  };

  const formatCurrency = (val: number) =>
    `$${val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography fontWeight={600}>{position.symbol}</Typography>
        </TableCell>
        <TableCell>{position.strategy}</TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            Kaufpreis: {formatCurrency(position.cost ?? 0)}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            Aktuell: {formatCurrency(position.market_value ?? 0)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            fontWeight={600}
            color={position.pnl >= 0 ? 'success.main' : 'error.main'}
          >
            {position.pnl >= 0 ? '+' : ''}{formatCurrency(position.pnl)}
            {' '}({(position.pnl_pct ?? 0) >= 0 ? '+' : ''}{(position.pnl_pct ?? 0).toFixed(1)}%)
          </Typography>
        </TableCell>
        <TableCell>{position.dte} Tage</TableCell>
        <TableCell>
          <Chip
            label={position.status === 'profit' ? 'Im Gewinn' : position.status === 'loss' ? 'Im Verlust' : 'Break-Even'}
            color={STATUS_COLORS[position.status] || 'default'}
            size="small"
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={() => setDialogOpen(true)}
            disabled={closing}
          >
            Schliessen
          </Button>
        </TableCell>
      </TableRow>

      {/* Close Order Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Position schliessen: {position.symbol}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <Box>
              <Typography variant="body2" gutterBottom fontWeight={600}>Order-Typ</Typography>
              <ToggleButtonGroup
                value={orderType}
                exclusive
                onChange={(_e, val) => { if (val) setOrderType(val); }}
                size="small"
                fullWidth
              >
                <ToggleButton value="AUTO">Auto (aktueller Preis)</ToggleButton>
                <ToggleButton value="MKT">MKT</ToggleButton>
                <ToggleButton value="LMT">LMT</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <TextField
              label="Limit-Preis"
              type="number"
              size="small"
              value={limitPrice}
              onChange={(e) => setLimitPrice(e.target.value)}
              disabled={orderType !== 'LMT'}
              required={orderType === 'LMT'}
              inputProps={{ step: 'any', min: 0 }}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={outsideRth}
                  onChange={(e) => setOutsideRth(e.target.checked)}
                />
              }
              label="Auch ausserhalb Handelszeiten"
            />
            <Typography variant="caption" color="text.secondary">
              Auto nimmt den aktuellen Marktpreis als Limit und verkauft auch ausserhalb der US-Handelszeiten (15:30-22:00 MEZ). MKT funktioniert nur waehrend regulaerer Handelszeit.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDialogConfirm}
            disabled={orderType === 'LMT' && !limitPrice || closing}
          >
            Verkaufen
          </Button>
        </DialogActions>
      </Dialog>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Eroeffnet: {position.opened_at} | Regime: {position.regime}
              </Typography>

              {/* Legs */}
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Legs:</Typography>
                {position.legs.map((leg, idx) => (
                  <Typography key={idx} variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {leg.action.toUpperCase()} {leg.qty}x {position.symbol} {leg.expiry} ${leg.strike} {leg.type} — Kauf: {formatCurrency(leg.entry_price)}{leg.market_price ? ` → Aktuell: ${formatCurrency(leg.market_price)}` : ''}
                  </Typography>
                ))}
              </Box>

              {/* Reason */}
              <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'action.hover' }}>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  {position.reason}
                </Typography>
              </Paper>

              {/* Exit Rules */}
              {position.exit_rules && position.exit_rules.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>Exit-Regeln:</Typography>
                  {position.exit_rules.map((rule, idx) => (
                    <Typography key={idx} variant="body2">
                      {rule.met ? '\u2705' : '\u274C'} {rule.label}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const IBKRPositions: React.FC = () => {
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [closingId, setClosingId] = useState<string | null>(null);

  const fetchPositions = useCallback(async () => {
    try {
      setError(null);
      const data = await ibkrService.getPositions();
      const raw = Array.isArray(data) ? data : (data?.positions || []);
      const mapped = raw.map((p: any) => ({
        id: p.id ?? '',
        symbol: p.symbol ?? '',
        strategy: p.strategy_name ?? p.strategy ?? '',
        opened_at: p.opened_at ?? '',
        regime: p.regime ?? p.entry_regime ?? '',
        pnl: p.pnl ?? p.current_pnl ?? 0,
        pnl_pct: p.pnl_pct ?? 0,
        cost: p.cost ?? 0,
        market_value: p.market_value ?? 0,
        max_profit: p.max_profit ?? 0,
        dte: typeof p.dte === 'number' ? p.dte : 0,
        status: p.status ?? ((p.pnl ?? 0) > 0 ? 'profit' : (p.pnl ?? 0) < 0 ? 'loss' : 'breakeven'),
        legs: (p.legs ?? []).map((l: any) => ({
          action: l.action, qty: l.qty ?? l.quantity ?? 1, strike: l.strike,
          type: l.right === 'C' ? 'Call' : l.right === 'P' ? 'Put' : l.type ?? '',
          expiry: l.expiry ?? '', entry_price: l.entry_price ?? l.premium ?? 0,
          market_price: l.market_price ?? 0,
        })),
        reason: p.signal_reason ?? p.reason ?? '',
      }));
      setPositions(mapped);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Positionen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPositions();
    const interval = setInterval(fetchPositions, 30000);
    return () => clearInterval(interval);
  }, [fetchPositions]);

  const handleClose = async (positionId: string, opts: CloseOpts) => {
    setClosingId(positionId);
    try {
      await ibkrService.closePosition(positionId, {
        orderType: opts.orderType,
        limitPrice: opts.limitPrice,
        outsideRth: opts.outsideRth,
      });
      await fetchPositions();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setClosingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Offene Positionen ({positions.length})
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {positions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Keine offenen Positionen</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50} />
                <TableCell>Symbol</TableCell>
                <TableCell>Strategie</TableCell>
                <TableCell>Wert</TableCell>
                <TableCell>P&L</TableCell>
                <TableCell>DTE</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {positions.map((pos) => (
                <PositionRow
                  key={pos.id}
                  position={pos}
                  onClose={handleClose}
                  closing={closingId === pos.id}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default IBKRPositions;
